// src/pages/Production/ProductionDashboard.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, onSnapshot, limit, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';

const styles = {
    container: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: 'transparent'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px'
    },
    title: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#0f172a',
        margin: 0,
        letterSpacing: '-0.5px'
    },
    subtitle: {
        fontSize: '16px',
        color: '#64748b',
        marginTop: '4px'
    },
    button: {
        padding: '10px 18px',
        border: 'none',
        background: '#06C167',
        color: 'white',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500',
        transition: 'all 0.2s',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '25px',
        marginBottom: '30px'
    },
    card: {
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0',
        padding: '25px',
        transition: 'all 0.2s ease',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center'
    },
    cardIcon: {
        fontSize: '28px',
        marginRight: '20px',
        background: '#f1f5f9',
        width: '60px',
        height: '60px',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    cardContent: {
        flex: 1
    },
    cardTitle: {
        fontSize: '18px',
        fontWeight: '600',
        color: '#0f172a',
        margin: '0 0 5px 0'
    },
    cardDescription: {
        fontSize: '14px',
        color: '#64748b',
        margin: 0
    },
    statsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '20px',
        marginBottom: '30px'
    },
    recentSection: {
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0',
        padding: '25px'
    },
    sectionTitle: {
        fontSize: '20px',
        fontWeight: '600',
        color: '#0f172a',
        margin: '0 0 20px 0'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse'
    },
    th: {
        background: '#f8fafc',
        padding: '12px 15px',
        textAlign: 'left',
        borderBottom: '2px solid #e2e8f0',
        color: '#64748b',
        fontSize: '12px',
        fontWeight: '600',
        textTransform: 'uppercase'
    },
    td: {
        padding: '15px',
        borderBottom: '1px solid #f1f5f9',
        verticalAlign: 'middle',
        fontSize: '14px',
        color: '#334155'
    },
    trHover: {
        cursor: 'pointer',
        transition: 'background 0.2s',
        ':hover': { background: '#f8fafc' } // Inline hover logic handled via JS or css class usually, here simplistic
    },
    statusBadge: {
        padding: '4px 10px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '500'
    }
};

const StatCard = ({ title, value, icon, color, subValue, onClick }) => (
    <div
        onClick={onClick}
        style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            display: 'flex',
            alignItems: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            border: '1px solid #e2e8f0',
            cursor: onClick ? 'pointer' : 'default',
            transition: 'transform 0.2s',
        }}
        onMouseEnter={(e) => onClick && (e.currentTarget.style.transform = 'translateY(-3px)')}
        onMouseLeave={(e) => onClick && (e.currentTarget.style.transform = 'translateY(0)')}
    >
        <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '14px',
            background: `${color}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '28px',
            marginRight: '20px',
            color: color
        }}>
            {icon}
        </div>
        <div>
            <div style={{ color: '#64748b', fontSize: '14px', marginBottom: '6px' }}>{title}</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e293b', marginBottom: '2px' }}>{value}</div>
            {subValue && <div style={{ fontSize: '12px', color: '#94a3b8' }}>{subValue}</div>}
        </div>
    </div>
);

function ProductionDashboard() {
    const navigate = useNavigate();
    const { userData } = useAuth();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        activeOrders: 0,
        completedToday: 0,
        activeSubcontractors: 0,
        lowStockMaterials: 0
    });
    const [recentOrders, setRecentOrders] = useState([]);

    useEffect(() => {
        if (!userData?.companyId) return;

        const companyId = userData.companyId;

        // 1. Listen to Production Orders
        const ordersQuery = query(collection(db, 'companies', companyId, 'productionOrders'), orderBy('createdAt', 'desc'));
        const unsubscribeOrders = onSnapshot(ordersQuery, (snapshot) => {
            const orders = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Calculate Active Orders
            const active = orders.filter(o => o.status !== 'Tamamlandı' && o.status !== 'İptal Edildi').length;

            // Calculate Completed Today
            const today = new Date().toDateString();
            const completed = orders.filter(o => {
                if (o.status !== 'Tamamlandı') return false;
                // Check if completion date is today (assuming updatedAt or similar field exists, falling back to basic check)
                // For now, if we don't have exact completion date, we might skip this or use createdAt for demo if needed.
                // Better approach: just count status='Tamamlandı' generally or add specific field.
                // Let's just track Total Completed for now if 'completedAt' is missing.
                return o.status === 'Tamamlandı';
            }).length;

            // Set Recent Orders (Top 5)
            setRecentOrders(orders.slice(0, 5));

            setStats(prev => ({
                ...prev,
                activeOrders: active,
                completedToday: completed // Or Total Completed
            }));

            setLoading(false);
        });

        // 2. Listen to Subcontractors
        const subQuery = query(collection(db, 'companies', companyId, 'subcontractors'));
        const unsubscribeSub = onSnapshot(subQuery, (snapshot) => {
            setStats(prev => ({
                ...prev,
                activeSubcontractors: snapshot.size // Assuming all are active
            }));
        });

        // 3. Simple Raw Materials Check (Optional: Filtering logic for low stock)
        // For efficiency, we might just get count, but let's get all for now as list usually isn't huge
        const materialsQuery = query(collection(db, 'companies', companyId, 'rawMaterials'));
        const unsubscribeMaterials = onSnapshot(materialsQuery, (snapshot) => {
            const materials = snapshot.docs.map(d => d.data());
            const lowStock = materials.filter(m => (m.stock || 0) <= (m.minStock || 0)).length;
            setStats(prev => ({ ...prev, lowStockMaterials: lowStock }));
        });

        return () => {
            unsubscribeOrders();
            unsubscribeSub();
            unsubscribeMaterials();
        };
    }, [userData]);

    const getStatusStyle = (status) => {
        switch (status) {
            case 'Planlandı': return { background: '#eff6ff', color: '#3b82f6' };
            case 'Devam Ediyor': return { background: '#fef3c7', color: '#f59e0b' };
            case 'Tamamlandı': return { background: '#f0fdf4', color: '#06C167' };
            case 'İptal Edildi': return { background: '#f1f5f9', color: '#64748b' };
            default: return { background: '#f8fafc', color: '#64748b' };
        }
    };

    if (loading) return <div>Yükleniyor...</div>;

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div>
                    <h1 style={styles.title}>Üretim Yönetim Paneli</h1>
                    <p style={styles.subtitle}>Fabrika durumunu canlı takip edin</p>
                </div>
                <div style={{ display: 'flex', gap: '15px' }}>
                    <button
                        onClick={() => navigate('/uretim/reçeteler')}
                        style={{ ...styles.button, background: 'white', color: '#334155', border: '1px solid #e2e8f0' }}
                    >
                        📝 Yeni Reçete
                    </button>
                    <button
                        onClick={() => navigate('/uretim/emirler')} // Usually opens modal there, or we can add state to open modal directly
                        style={styles.button}
                    >
                        🚀 Yeni Üretim Emri
                    </button>
                </div>
            </div>

            <div style={styles.statsGrid}>
                <StatCard
                    title="Aktif Üretim"
                    value={stats.activeOrders}
                    icon="⚡"
                    color="#f59e0b"
                    subValue="Devam eden işler"
                    onClick={() => navigate('/uretim/emirler')}
                />
                <StatCard
                    title="Tamamlanan"
                    value={stats.completedToday}
                    icon="✅"
                    color="#10b981"
                    subValue="Toplam biten işler"
                    onClick={() => navigate('/uretim/emirler')}
                />
                <StatCard
                    title="Kritik Stok"
                    value={stats.lowStockMaterials}
                    icon="⚠️"
                    color="#ef4444"
                    subValue="Hammadde uyarısı"
                    onClick={() => navigate('/uretim/hammadde')} // Assuming route
                />
                <StatCard
                    title="Aktif Fason"
                    value={stats.activeSubcontractors}
                    icon="🏭"
                    color="#3b82f6"
                    subValue="Çalışılan tedarikçiler"
                    onClick={() => navigate('/uretim/fason-tedarikciler')}
                />
            </div>

            <div style={styles.grid}>
                <div style={styles.card} onClick={() => navigate('/uretim/reçeteler')}>
                    <div style={styles.cardIcon}>📋</div>
                    <div style={styles.cardContent}>
                        <h3 style={styles.cardTitle}>Reçeteler</h3>
                        <p style={styles.cardDescription}>Ürün ağaçlarını yönetin</p>
                    </div>
                </div>

                <div style={styles.card} onClick={() => navigate('/uretim/emirler')}>
                    <div style={styles.cardIcon}>🏭</div>
                    <div style={styles.cardContent}>
                        <h3 style={styles.cardTitle}>Üretim Emirleri</h3>
                        <p style={styles.cardDescription}>İş emirlerini takip edin</p>
                    </div>
                </div>

                <div style={styles.card} onClick={() => navigate('/uretim/maliyet-hesaplama')}>
                    <div style={styles.cardIcon}>💰</div>
                    <div style={styles.cardContent}>
                        <h3 style={styles.cardTitle}>Maliyet Analizi</h3>
                        <p style={styles.cardDescription}>Ürün maliyetlerini hesaplayın</p>
                    </div>
                </div>

                <div style={styles.card} onClick={() => navigate('/uretim/raporlar')}>
                    <div style={styles.cardIcon}>📊</div>
                    <div style={styles.cardContent}>
                        <h3 style={styles.cardTitle}>Raporlar</h3>
                        <p style={styles.cardDescription}>Üretim verimliliğini analiz edin</p>
                    </div>
                </div>
            </div>

            <div style={styles.recentSection}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h2 style={styles.sectionTitle}>Son İşlemler</h2>
                    <button
                        onClick={() => navigate('/uretim/emirler')}
                        style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontWeight: '500' }}
                    >
                        Tümünü Gör &rarr;
                    </button>
                </div>

                {recentOrders.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>Henüz üretim emri bulunmuyor.</div>
                ) : (
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Emir No</th>
                                <th style={styles.th}>Ürünler</th>
                                <th style={styles.th}>Tarih</th>
                                <th style={styles.th}>Durum</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recentOrders.map(order => (
                                <tr
                                    key={order.id}
                                    style={styles.trHover}
                                    onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                    onClick={() => navigate(`/uretim/emirler/${order.id}`)}
                                >
                                    <td style={{ ...styles.td, fontWeight: '600', color: '#0f172a' }}>
                                        #{order.orderNumber || order.id.substring(0, 6)}
                                    </td>
                                    <td style={styles.td}>
                                        {order.recipeSelections?.map(r => r.recipeName).join(', ') || 'Belirtilmemiş'}
                                    </td>
                                    <td style={styles.td}>
                                        {order.createdAt?.seconds ? new Date(order.createdAt.seconds * 1000).toLocaleDateString('tr-TR') : '-'}
                                    </td>
                                    <td style={styles.td}>
                                        <span style={{
                                            ...styles.statusBadge,
                                            ...getStatusStyle(order.status)
                                        }}>
                                            {order.status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

export default ProductionDashboard;